import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Compass, Download, FileText, Loader2, Sparkles, Target, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, saveActivityToDrive } from "../lib/api";
import PostGenScheduleDialog from "./PostGenScheduleDialog";

const STRANDS_FALLBACK = [
  { value: "Number", letter: "B" },
  { value: "Algebra", letter: "C" },
  { value: "Data", letter: "D" },
  { value: "Spatial Sense", letter: "E" },
  { value: "Financial Literacy", letter: "F" },
];

const GRADES = ["Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];

const STRUCTURES = [
  { value: "parallel", label: "Parallel Task", blurb: "Two versions of the same problem. Students self-select an entry point." },
  { value: "open", label: "Open Task", blurb: "One problem, many valid answers. Consolidation uses the range." },
  { value: "three_part", label: "Three-Part Investigation", blurb: "Getting Started · Working On It · Discussing Our Thinking." },
];

const TIME_BLOCKS = [
  { value: "45", label: "45 minutes" },
  { value: "60", label: "60 minutes" },
  { value: "80", label: "80 minutes" },
];

const CONTEXT_TYPES = [
  { value: "auto", label: "Atomic Edu chooses" },
  { value: "real_world", label: "Real-world" },
  { value: "abstract", label: "Abstract / number-based" },
  { value: "visual", label: "Visual / spatial" },
];

const LFHC_QUANTITIES = [
  { value: 1, label: "1 question" },
  { value: 3, label: "Bank of 3" },
  { value: 5, label: "Bank of 5" },
];

const POLL_MS = 3000;
const JOBS_STORAGE_KEY = "atomicedu.taskbuilder.jobs.v1";
const JOBS_STORAGE_LIMIT = 10;

const Section = ({ label, hint, children, testid }) => (
  <div className="mb-5" data-testid={testid}>
    <Label className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984] mb-2 block">
      {label}
    </Label>
    {hint && <p className="text-xs text-[#4a6984]/65 mb-2 font-medium">{hint}</p>}
    {children}
  </div>
);

const Pill = ({ children, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs font-extrabold transition border ${
      active
        ? "bg-[#1c2d3d] text-white border-[#1c2d3d]"
        : "bg-white text-[#4a6984] border-[#4a6984]/20 hover:bg-[#f4f5f7]"
    }`}
  >
    {children}
  </button>
);

// ============================================================
// Task Builder — Extended Rich Task + Single LFHC Question
// ============================================================
export default function TaskBuilder({ mode: initialMode = "extended", onClose }) {
  const [mode, setMode] = useState(initialMode); // 'extended' | 'lfhc'

  // Shared selectors
  const [grade, setGrade] = useState("Grade 5");
  const [strand, setStrand] = useState("Number");
  const [strands, setStrands] = useState(STRANDS_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/curriculum");
        const byGrade = data?.strands || {};
        let mathStrands = null;
        for (const g of Object.keys(byGrade)) {
          const m = byGrade[g]?.["Mathematics"];
          if (Array.isArray(m) && m.length) { mathStrands = m; break; }
        }
        const mapped = (mathStrands || [])
          .filter((s) => ["B", "C", "D", "E", "F"].includes(s.code))
          .map((s) => ({ value: s.strand_name || s.name, letter: s.code }));
        if (!cancelled && mapped.length) setStrands(mapped);
      } catch (e) {
        /* keep STRANDS_FALLBACK — never render an empty strand picker */
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [expectations, setExpectations] = useState([]);
  const [expectationsLoading, setExpectationsLoading] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState([]); // for extended (1-2)
  const [lfhcCode, setLfhcCode] = useState(""); // for LFHC (single)

  // Extended-only
  const [structure, setStructure] = useState("open");
  const [timeBlock, setTimeBlock] = useState("60");
  const [contextType, setContextType] = useState("auto");

  // LFHC-only
  const [lfhcQuantity, setLfhcQuantity] = useState(1);

  const [submitting, setSubmitting] = useState(false);
  // Phase 88 — most recently-completed task to drive the post-gen
  // Planner popup. Mirrors the Generator's behaviour.
  const [justGenerated, setJustGenerated] = useState(null);
  // Jobs: each entry is { job_id, kind, status, activity?, error? }.
  // Done jobs are persisted to localStorage so refreshing /math-engine
  // doesn't lose the teacher's downloads. Running jobs ARE NOT persisted
  // because polling state is meaningless after a refresh — we'd be left
  // showing a phantom spinner. We cap at the most recent 10 done jobs.
  const [jobs, setJobs] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(JOBS_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Defensive: keep only "done" jobs with an activity. Toss anything else.
      return parsed
        .filter((j) => j && j.status === "done" && j.activity && j.job_id)
        .slice(0, JOBS_STORAGE_LIMIT);
    } catch {
      return [];
    }
  });

  // Persist the done jobs whenever they change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const toStore = jobs
        .filter((j) => j.status === "done" && j.activity)
        .slice(0, JOBS_STORAGE_LIMIT);
      window.localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(toStore));
    } catch {
      // Quota / private mode — fail silently.
    }
  }, [jobs]);

  // Recovery fetch on mount — pull the teacher's recent Task Builder
  // outputs from the server and merge any we don't already have in
  // local state. This rescues tasks that completed on the backend
  // but never bubbled into the UI (e.g. the v1 polling bug that read
  // `data.result.activity` instead of `data.activity`).
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const { data } = await api.get("/math-engine/task/recent", { params: { limit: 10 } });
        const remoteJobs = data?.jobs || [];
        if (abort || remoteJobs.length === 0) return;
        setJobs((prev) => {
          const seenActivityIds = new Set(prev.map((j) => j.activity?.id).filter(Boolean));
          const fresh = remoteJobs.filter((rj) => !seenActivityIds.has(rj.activity?.id));
          if (fresh.length === 0) return prev;
          // Resumed jobs come AFTER any in-flight local jobs.
          return [...prev, ...fresh].slice(0, JOBS_STORAGE_LIMIT);
        });
      } catch {
        // Endpoint missing on older deploys — silently no-op.
      }
    })();
    return () => {
      abort = true;
    };
  }, []);

  // Load expectations whenever grade/strand changes (reuses worksheet endpoint).
  useEffect(() => {
    let abort = false;
    setExpectationsLoading(true);
    setSelectedCodes([]);
    setLfhcCode("");
    (async () => {
      try {
        const { data } = await api.get("/math-engine/worksheet/expectations", {
          params: { grade, strand },
        });
        if (abort) return;
        setExpectations(data?.expectations || []);
      } catch (e) {
        if (!abort) toast.error("Couldn't load expectations.");
      } finally {
        if (!abort) setExpectationsLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [grade, strand]);

  const toggleCode = (code) =>
    setSelectedCodes((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      // Extended Rich Task targets 1-2 codes, never more.
      if (prev.length >= 2) return [prev[1], code];
      return [...prev, code];
    });

  const canSubmitExtended = selectedCodes.length >= 1 && !submitting;
  const canSubmitLfhc = !!lfhcCode && !submitting;
  const anyRunning = jobs.some((j) => j.status === "running");

  const pollJob = async (job_id, kind) => {
    try {
      const { data } = await api.get(`/jobs/${job_id}`);
      if (data?.status === "done" && data?.activity) {
        setJobs((prev) =>
          prev.map((j) => (j.job_id === job_id ? { ...j, status: "done", activity: data.activity } : j))
        );
        // Phase 88 — surface the Planner popup the first time this
        // activity transitions to done in the current session.
        setJustGenerated(data.activity);
        toast.success(kind === "extended" ? "Rich task ready." : "Low-Floor, High-Ceiling questions ready.");
        return true;
      }
      if (data?.status === "error") {
        setJobs((prev) =>
          prev.map((j) => (j.job_id === job_id ? { ...j, status: "error", error: data.error || "Failed" } : j))
        );
        toast.error(data?.error || "Generation failed.");
        return true;
      }
    } catch {
      // Keep polling — job may still be propagating.
    }
    return false;
  };

  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(async () => {
      for (const j of jobs) {
        if (j.status === "running") await pollJob(j.job_id, j.kind);
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [anyRunning, jobs]);

  const submitExtended = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/math-engine/task/generate-extended", {
        grade,
        strand,
        expectation_codes: selectedCodes,
        structure,
        time_block: timeBlock,
        context_type: contextType,
      });
      setJobs((prev) => [
        ...prev,
        { job_id: data.job_id, kind: "extended", status: "running" },
      ]);
      toast.info("Building your rich task… a minute or two.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the job.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitLfhc = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/math-engine/task/generate-lfhc", {
        grade,
        strand,
        expectation_code: lfhcCode,
        quantity: lfhcQuantity,
      });
      setJobs((prev) => [
        ...prev,
        { job_id: data.job_id, kind: "lfhc", status: "running" },
      ]);
      toast.info(`Building ${lfhcQuantity === 1 ? "your question" : `your bank of ${lfhcQuantity}`}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the job.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="task-builder">
      <div className="flex items-center justify-between">
        <div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-xs font-extrabold text-[#4a6984] hover:text-[#1c2d3d] mb-1.5"
            data-testid="task-back"
          >
            <ArrowLeft className="h-3 w-3" /> Back to Math Engine
          </button>
          <h2 className="font-black text-2xl text-[#1c2d3d] tracking-tight">Task Builder</h2>
          <p className="text-sm text-[#4a6984]/85 mt-0.5">
            Generate a rich math task — a full investigation, or a single low-floor / high-ceiling question.
          </p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setMode("extended")}
          data-testid="task-mode-extended"
          className={`ae-card p-5 text-left transition ${
            mode === "extended" ? "border-[#96ded1] shadow-md ring-2 ring-[#96ded1]/40" : "hover:border-[#96ded1] hover:shadow-md"
          }`}
        >
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 rounded-lg bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d] shrink-0">
              <Compass className="h-5 w-5" />
            </span>
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">Task Builder</div>
              <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight mb-1">Extended Rich Task</h3>
              <p className="text-sm text-[#4a6984]/85 leading-snug">
                A full classroom-period task. Parallel, Open, or Three-Part Investigation. Two documents — student + teacher.
              </p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setMode("lfhc")}
          data-testid="task-mode-lfhc"
          className={`ae-card p-5 text-left transition ${
            mode === "lfhc" ? "border-[#96ded1] shadow-md ring-2 ring-[#96ded1]/40" : "hover:border-[#96ded1] hover:shadow-md"
          }`}
        >
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 rounded-lg bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d] shrink-0">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">Task Builder</div>
              <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight mb-1">Low-Floor, High-Ceiling Question</h3>
              <p className="text-sm text-[#4a6984]/85 leading-snug">
                One strong question — or a bank of 3 / 5 — with floor, ceiling, sample answers, and a stuck-student prompt.
              </p>
            </div>
          </div>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* FORM */}
        <div className="ae-card p-6 lg:col-span-2">
          <h3 className="font-black text-xl text-[#1c2d3d] mb-5 tracking-tight">
            {mode === "extended" ? "Build the task" : "Build the question"}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Section label="Grade">
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger data-testid="task-grade" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRADES.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Section>
            <Section label="Strand">
              <Select value={strand} onValueChange={setStrand}>
                <SelectTrigger data-testid="task-strand" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {strands.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Section>
          </div>

          {mode === "extended" ? (
            <>
              <Section
                label={`Specific expectation${selectedCodes.length > 1 ? "s" : ""} · ${selectedCodes.length} selected`}
                hint="Pick 1 or 2 expectations the task targets."
                testid="task-expectations-section"
              >
                <ExpectationList
                  expectations={expectations}
                  loading={expectationsLoading}
                  grade={grade}
                  strand={strand}
                  selected={selectedCodes}
                  onToggle={toggleCode}
                />
              </Section>

              <Section label="Task structure" hint="How the rich task is organised.">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {STRUCTURES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setStructure(s.value)}
                      data-testid={`task-structure-${s.value}`}
                      className={`text-left rounded-lg p-3 border transition ${
                        structure === s.value
                          ? "bg-[#96ded1]/15 border-[#96ded1]"
                          : "bg-white border-[#4a6984]/15 hover:bg-[#f4f5f7]"
                      }`}
                    >
                      <div className="font-extrabold text-sm text-[#1c2d3d] mb-0.5">{s.label}</div>
                      <div className="text-xs text-[#4a6984]/85 leading-snug">{s.blurb}</div>
                    </button>
                  ))}
                </div>
              </Section>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Section label="Time block">
                  <Select value={timeBlock} onValueChange={setTimeBlock}>
                    <SelectTrigger data-testid="task-time-block" className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_BLOCKS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Section>
                <Section label="Context type" hint="What kind of wrapper the task uses.">
                  <Select value={contextType} onValueChange={setContextType}>
                    <SelectTrigger data-testid="task-context-type" className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTEXT_TYPES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Section>
              </div>

              <Button
                onClick={submitExtended}
                disabled={!canSubmitExtended}
                data-testid="task-generate-extended"
                className="w-full h-12 bg-[#1c2d3d] text-white hover:bg-[#1c2d3d]/90 rounded-lg font-extrabold text-sm"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</>
                ) : (
                  <><Wand2 className="h-4 w-4 mr-2" /> Generate Task</>
                )}
              </Button>
            </>
          ) : (
            <>
              <Section
                label="Specific expectation"
                hint="Pick the single expectation this question targets."
                testid="task-lfhc-expectation-section"
              >
                <ExpectationList
                  expectations={expectations}
                  loading={expectationsLoading}
                  grade={grade}
                  strand={strand}
                  selected={lfhcCode ? [lfhcCode] : []}
                  onToggle={(code) => setLfhcCode((cur) => (cur === code ? "" : code))}
                  singleSelect
                />
              </Section>

              <Section label="Quantity" hint="One strong question, or a bank to choose from.">
                <div className="flex flex-wrap gap-2">
                  {LFHC_QUANTITIES.map((q) => (
                    <Pill
                      key={q.value}
                      active={lfhcQuantity === q.value}
                      onClick={() => setLfhcQuantity(q.value)}
                      testid={`task-lfhc-qty-${q.value}`}
                    >
                      {q.label}
                    </Pill>
                  ))}
                </div>
              </Section>

              <Button
                onClick={submitLfhc}
                disabled={!canSubmitLfhc}
                data-testid="task-generate-lfhc"
                className="w-full h-12 bg-[#1c2d3d] text-white hover:bg-[#1c2d3d]/90 rounded-lg font-extrabold text-sm"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Generate Question{lfhcQuantity > 1 ? "s" : ""}</>
                )}
              </Button>
            </>
          )}
        </div>

        {/* RESULTS */}
        <div className="ae-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-lg text-[#1c2d3d] tracking-tight">Your tasks</h3>
            {jobs.length > 0 && (
              <button
                type="button"
                onClick={() => setJobs([])}
                data-testid="task-jobs-clear"
                className="text-[10px] font-extrabold uppercase tracking-wider text-[#4a6984]/70 hover:text-[#1c2d3d] transition"
              >
                Clear
              </button>
            )}
          </div>
          {jobs.length === 0 ? (
            <p className="text-sm text-[#4a6984]/70">
              Generated tasks land here. Extended Rich Tasks produce a student document AND a teacher facilitation guide.
            </p>
          ) : (
            <div className="space-y-3">
              {jobs.map((j) => (
                <TaskJobCard key={j.job_id} job={j} />
              ))}
            </div>
          )}
          {jobs.some((j) => j.status === "done") && (
            <p className="text-xs text-[#4a6984]/65 mt-4 font-medium">
              All tasks are saved to your <strong>My Library</strong> automatically.
            </p>
          )}
        </div>
      </div>
      {/* Phase 88 — Pro-tier post-generation Planner popup. Silently
          skips when the user isn't a Pro Planner subscriber. */}
      {justGenerated && (
        <PostGenScheduleDialog
          key={justGenerated.id}
          activity={justGenerated}
          onDismiss={() => setJustGenerated(null)}
        />
      )}
    </div>
  );
}

// ----- Helpers -----

const ExpectationList = ({ expectations, loading, grade, strand, selected, onToggle, singleSelect }) => (
  <div className="rounded-lg border border-[#4a6984]/15 bg-white max-h-72 overflow-y-auto p-2">
    {loading ? (
      <div className="text-sm text-[#4a6984]/70 p-3">Loading expectations…</div>
    ) : expectations.length === 0 ? (
      <div className="text-sm text-[#4a6984]/70 p-3">
        No expectations found for {grade} → {strand}.
      </div>
    ) : (
      expectations.map((e) => {
        const on = selected.includes(e.code);
        return (
          <label
            key={e.code}
            className={`flex items-start gap-3 p-2 rounded-md cursor-pointer transition ${
              on ? "bg-[#96ded1]/15" : "hover:bg-[#f4f5f7]"
            }`}
          >
            <Checkbox
              checked={on}
              onCheckedChange={() => onToggle(e.code)}
              data-testid={`task-exp-${e.code}`}
            />
            <div className="text-sm">
              <div className="font-extrabold text-[#1c2d3d]">
                {e.code} <span className="text-[#4a6984]/70 font-semibold">· {e.topic}</span>
              </div>
              <div className="text-[#4a6984]/85">{e.description}</div>
            </div>
          </label>
        );
      })
    )}
  </div>
);

const TaskJobCard = ({ job }) => {
  const a = job.activity;
  const isExtended = job.kind === "extended";
  const downloadHref = (fmt) =>
    a ? `${process.env.REACT_APP_BACKEND_URL}/api/activities/${a.id}/export?format=${fmt}` : "#";
  const [savingDrive, setSavingDrive] = useState(null);

  const saveToDrive = async (fmt, label) => {
    if (!a?.id) return;
    setSavingDrive(fmt);
    try {
      const res = await saveActivityToDrive(a.id, fmt);
      if (res?.webViewLink) {
        window.open(res.webViewLink, "_blank", "noopener,noreferrer");
        toast.success(`${label} saved to Google Drive.`);
      } else {
        toast.success(`${label} saved.`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 403 && /drive/i.test(detail)) {
        toast.error("Connect Google Drive first (Account Settings → Integrations).");
      } else {
        toast.error(detail || "Couldn't save to Google Drive.");
      }
    } finally {
      setSavingDrive(null);
    }
  };

  return (
    <div
      className="rounded-lg border border-[#4a6984]/15 bg-[#f4f5f7]/50 p-4"
      data-testid={`task-job-${job.kind}-${job.job_id.slice(0, 6)}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#1c2d3d] bg-[#96ded1]/35 px-2 py-0.5 rounded-full">
          {isExtended ? "Rich Task" : "LFHC Question"}
        </span>
        {job.resumed && (
          <span
            className="text-[10px] font-black uppercase tracking-[0.16em] text-[#4a6984] bg-[#f4f5f7] border border-[#4a6984]/15 px-2 py-0.5 rounded-full"
            data-testid="task-job-resumed-badge"
          >
            Resumed
          </span>
        )}
        {job.status === "running" && (
          <span className="inline-flex items-center gap-1.5 text-xs text-[#4a6984] font-semibold">
            <Loader2 className="h-3 w-3 animate-spin" /> Building…
          </span>
        )}
        {job.status === "error" && (
          <span className="text-xs text-red-600 font-semibold">Failed</span>
        )}
      </div>
      {job.status === "done" && a && (
        <>
          <p className="font-extrabold text-sm text-[#1c2d3d] mb-3 leading-snug">
            {a.activity_name}
          </p>
          <div className="flex flex-wrap gap-2">
            {isExtended ? (
              <>
                <a
                  href={downloadHref("task_student")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1c2d3d] text-white text-xs font-extrabold hover:bg-[#1c2d3d]/90"
                  data-testid="task-dl-student"
                >
                  <Download className="h-3 w-3" /> Student PDF
                </a>
                <a
                  href={downloadHref("task_teacher")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7]"
                  data-testid="task-dl-teacher"
                >
                  <FileText className="h-3 w-3" /> Teacher PDF
                </a>
                <a
                  href={downloadHref("task_student_docx")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7]"
                  data-testid="task-dl-student-docx"
                >
                  <FileText className="h-3 w-3" /> Student .docx
                </a>
                <a
                  href={downloadHref("task_teacher_docx")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7]"
                  data-testid="task-dl-teacher-docx"
                >
                  <FileText className="h-3 w-3" /> Teacher .docx
                </a>
                <button
                  type="button"
                  onClick={() => saveToDrive("gdoc_task_student", "Student task")}
                  disabled={savingDrive === "gdoc_task_student"}
                  data-testid="task-gdoc-student"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7] disabled:opacity-50"
                >
                  {savingDrive === "gdoc_task_student" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                  Save Student to Google Docs
                </button>
                <button
                  type="button"
                  onClick={() => saveToDrive("gdoc_task_teacher", "Teacher guide")}
                  disabled={savingDrive === "gdoc_task_teacher"}
                  data-testid="task-gdoc-teacher"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7] disabled:opacity-50"
                >
                  {savingDrive === "gdoc_task_teacher" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                  Save Teacher to Google Docs
                </button>
              </>
            ) : (
              <>
                <a
                  href={downloadHref("task_lfhc_student")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1c2d3d] text-white text-xs font-extrabold hover:bg-[#1c2d3d]/90"
                  data-testid="task-dl-lfhc-student"
                >
                  <Download className="h-3 w-3" /> Student Sheet PDF
                </a>
                <a
                  href={downloadHref("task_lfhc")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7]"
                  data-testid="task-dl-lfhc"
                >
                  <Download className="h-3 w-3" /> Teacher Guide PDF
                </a>
                <a
                  href={downloadHref("task_lfhc_docx")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7]"
                  data-testid="task-dl-lfhc-docx"
                >
                  <FileText className="h-3 w-3" /> Teacher Word
                </a>
                <button
                  type="button"
                  onClick={() => saveToDrive("gdoc_task_lfhc", "Question bank")}
                  disabled={savingDrive === "gdoc_task_lfhc"}
                  data-testid="task-gdoc-lfhc"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] text-xs font-extrabold hover:bg-[#f4f5f7] disabled:opacity-50"
                >
                  {savingDrive === "gdoc_task_lfhc" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                  Save to Google Docs
                </button>
              </>
            )}
          </div>
        </>
      )}
      {job.status === "error" && (
        <p className="text-xs text-red-600/85 mt-2">{job.error || "Something went wrong."}</p>
      )}
    </div>
  );
};
